import { useEffect, useState } from 'react'

import { ActionIcon, Collapse, Select } from '@mantine/core'
import { Check, X } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_COMPANY, UPDATE_COMPANY } from 'src/graphql/companies.graphql'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconCopy from 'src/icons/IconCopy'
import IconEdit from 'src/icons/IconEdit'

import FieldLabel from './FieldLabel'

interface AtsEmailTrackingCardProps {
  companyData: {
    companyId: string
    atsEmailTrackingEnabled: boolean
    atsInboundEmailAddress: string | null
  }
  initialOpened?: boolean
}

const SELECT_OPTIONS = [
  { value: 'yes', label: 'Enabled' },
  { value: 'no', label: 'Disabled' },
] as const

const AtsEmailTrackingCard = ({
  companyData,
  initialOpened = false,
}: AtsEmailTrackingCardProps) => {
  const { currentUser } = useAuth()
  const canEdit = isAdminOrOps(currentUser)

  const [enabled, setEnabled] = useState(companyData.atsEmailTrackingEnabled)
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState(initialOpened)
  const [copySuccess, setCopySuccess] = useState(false)

  const [updateCompany, { loading }] = useMutation(UPDATE_COMPANY, {
    onCompleted: () => {
      toast('ATS email tracking settings updated', 'success')
      setEditing(false)
    },
    onError: (error) => {
      toast(`Failed to update settings: ${error.message}`, 'error')
    },
    refetchQueries: [
      {
        query: GET_COMPANY,
        variables: { id: companyData.companyId },
      },
    ],
    awaitRefetchQueries: true,
  })

  useEffect(() => {
    if (!editing) {
      setEnabled(companyData.atsEmailTrackingEnabled)
    }
  }, [companyData, editing])

  const handleSave = async () => {
    await updateCompany({
      variables: {
        id: companyData.companyId,
        input: {
          atsEmailTrackingEnabled: enabled,
        },
      },
    })
  }

  const handleCancel = () => {
    setEnabled(companyData.atsEmailTrackingEnabled)
    setEditing(false)
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (error) {
      toast('Failed to copy to clipboard', 'error')
    }
  }

  const enableLabel = (
    <FieldLabel
      label="Enable ATS Email Tracking"
      tooltip="When enabled, Double Nickel captures Tenstreet email notifications and automatically updates VoE status."
    />
  )

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3">
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        ATS Email Tracking
        {canEdit && !editing && (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              setEditing(true)
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        )}
        {canEdit && editing && (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={loading}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
          </div>
        )}
      </div>

      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-12 gap-3">
            {/* Enable toggle */}
            <div className="col-span-3">
              {editing ? (
                <Select
                  label={enableLabel}
                  value={enabled ? 'yes' : 'no'}
                  onChange={(val) => setEnabled(val === 'yes')}
                  data={SELECT_OPTIONS}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                    input: 'text-sm',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={enableLabel}
                  value={enabled ? 'Enabled' : 'Disabled'}
                />
              )}
            </div>

            {/* Inbound email address (read-only always, with copy) */}
            <div className="col-span-6">
              <div className="flex flex-col gap-1">
                <FieldLabel
                  label="Inbound Email Address"
                  tooltip="Add this email as a CC recipient in your Tenstreet Xchange notification settings."
                />
                {companyData.atsInboundEmailAddress ? (
                  <div className="flex items-center gap-1">
                    <div className="flex-1 rounded border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-3 py-[7px] font-mono text-sm text-doubleNickel-gray-700">
                      {companyData.atsInboundEmailAddress}
                    </div>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => {
                        if (companyData.atsInboundEmailAddress) {
                          copyToClipboard(companyData.atsInboundEmailAddress)
                        }
                      }}
                      title="Copy email address"
                    >
                      {copySuccess ? (
                        <IconCheckmark className="h-3 w-3 fill-none stroke-green-600" />
                      ) : (
                        <IconCopy className="h-3 w-3 fill-none stroke-doubleNickel-gray-500" />
                      )}
                    </ActionIcon>
                  </div>
                ) : (
                  <div className="text-sm text-doubleNickel-gray-500">
                    Not configured yet — contact support to provision an inbound
                    email address.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Setup instructions */}
          {companyData.atsInboundEmailAddress && (
            <div className="rounded border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-3 py-2 text-xs leading-relaxed text-doubleNickel-gray-500">
              <span className="font-semibold text-doubleNickel-gray-600">
                Setup:
              </span>{' '}
              Add this email address as a CC recipient in your Tenstreet Xchange
              notification settings. If Tenstreet doesn&apos;t support a CC
              address, set up a Gmail auto-forward rule from{' '}
              <span className="font-semibold text-doubleNickel-gray-600">
                noreply@tenstreet.com
              </span>{' '}
              to this address.
            </div>
          )}
        </div>
      </Collapse>
    </div>
  )
}

export default AtsEmailTrackingCard
